import React, {useEffect, useMemo} from 'react';
import type {FCWithChildren} from 'types';
import type {
  CodeListId,
  CombinedCodeListId,
  LocalCodeListId,
} from 'services/codelists/types';
import {CombinedCodeListIds, PartCodeLists} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {selectSettingsPreferredLocale} from 'services/settings';
import type {SetLoadStatus} from 'services/utils/hooks';
import {useBatchedLoadStatus} from 'services/utils/hooks';
import {useAppSelector, useAppStore} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';
import {codelistMaxAgeSeconds, useGetCodelistQuery} from '../codelistApi';
import {selectCodelistFromCache} from '../codelistSelectors';
import {mockCodeLists} from '../consts';

type Props = {
  codeLists: (CodeListId | LinkedRoleCodeListId)[];
};

const isLoadableFromBackend = (
  id: CodeListId | LinkedRoleCodeListId,
): id is CodeListId =>
  !isLinkedRoleCodeListId(id) &&
  !AllLocalCodeListIds.includes(id as LocalCodeListId) &&
  !mockCodeLists.includes(id as CodeListId);

/**
 * Triggers and subscribes to a single codelist via RTK Query. The subscription
 * keeps the cache entry alive while the gate is mounted so consumer hooks can
 * read it.
 */
const CodelistLoader: React.FC<{
  id: CodeListId;
  language: string;
  onStatus: SetLoadStatus;
}> = ({id, language, onStatus}) => {
  const {data, isError} = useGetCodelistQuery(
    {id, language},
    {
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: codelistMaxAgeSeconds,
    },
  );

  useEffect(() => {
    // Reuse old data if it exist. A loaded codelist is never reported as failed.
    if (data) {
      onStatus(id, 'Loaded');
    } else if (isError) {
      onStatus(id, 'Failed');
    } else {
      onStatus(id, 'Loading');
    }
  }, [data, id, isError, onStatus]);

  return null;
};

export const CodeListsGate: FCWithChildren<Props> = ({
  codeLists: givenCodeLists,
  children,
}) => {
  const {t} = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const store = useAppStore();

  const givenIdsKey = givenCodeLists.join('|');
  const loadIds = useMemo<CodeListId[]>(() => {
    const given = (givenIdsKey === '' ? [] : givenIdsKey.split('|')) as (
      | CodeListId
      | LinkedRoleCodeListId
    )[];
    const expanded = new Set<CodeListId>();
    given.forEach(id => {
      if (CombinedCodeListIds.includes(id as CombinedCodeListId)) {
        PartCodeLists[id as CombinedCodeListId].forEach(partId => {
          if (isLoadableFromBackend(partId)) {
            expanded.add(partId);
          }
        });
      } else if (isLoadableFromBackend(id)) {
        expanded.add(id as CodeListId);
      }
    });

    return Array.from(expanded);
  }, [givenIdsKey]);

  const notLoadedIds = useMemo<CodeListId[]>(() => {
    const state = store.getState();
    return loadIds.filter(id => !selectCodelistFromCache(id, language, state));
  }, [loadIds, language, store]);

  const {status, setStatus} = useBatchedLoadStatus(notLoadedIds);

  return (
    <>
      {loadIds.map(id => (
        <CodelistLoader
          key={id}
          id={id}
          language={language}
          onStatus={setStatus}
        />
      ))}
      {status === 'Failed' ? (
        <SceneError error={t('error.codelist.failedToLoad')} applySceneHeight />
      ) : status === 'Loading' ? (
        <SceneLoader
          loadingType={t('general.entity.codelist')}
          applySceneHeight
        />
      ) : (
        children
      )}
    </>
  );
};
